/**
* Copyright by: 

* by ESS-Ehrhardt Software Solutions & Service GmbH
* Basselscheider Str. 55
* D-56281 Emmelshausen
* +49 (0) 67 42 / 87 27 0
* info@epg.com
*/
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AuthService } from '@epg/angular-base-lib';
import { ButtonAction, ConfirmDeleteDialogData, ConfirmDialogService } from '@epg/ng-components';
import { PublicTenantManagerClient, TenantRoleDto } from '@epg/tenant-manager-client';
import { faPencilAlt } from '@fortawesome/pro-regular-svg-icons';
import { Subscription } from 'rxjs';
import {
  getActiveTenantFromSessionStorage,
  opencanDeactivateDialog,
  toTitleCase
} from '../../../../app/shared/functions';
import { CanComponentDeactivate } from '../../../../app/shared/interface/can-component-deactivate.interface';
import { OneAppPermission } from '../../../../enums/permission.enum';
import { EmployeeGroupModel } from '../../../../models/employee-group.model';
import { EmployeeModel } from '../../../../models/employee.model';
import { EmployeeService } from '../../../../services/employee.service';

@Component({
  selector: 'app-employee-detail',
  templateUrl: './employee-detail.component.html',
  styleUrls: ['./employee-detail.component.scss'],
  standalone: false
})
// TODO: dont allow to change the email if app login is authorized.
export class EmployeeDetailComponent implements OnInit, OnDestroy, CanComponentDeactivate {
  tenantApplicationName = 'epg-tenant-manager';
  employeeChangedSubscription: Subscription = new Subscription();
  newEmployeeSubscription: Subscription = new Subscription();
  employeeGroupsChangedSubscription: Subscription = new Subscription();
  routeSub: Subscription = new Subscription();
  employeeRouteChangeSub: Subscription = new Subscription();
  rolesChangedSubscription: Subscription = new Subscription();

  faEdit = faPencilAlt;
  employee!: EmployeeModel;
  employeeForm!: FormGroup;
  employeeGroups: EmployeeGroupModel[] = [];
  employeeId = '';
  roles: TenantRoleDto[] = [];
  userRoles: string[] = [];
  currentUserRoles: string[] = [];
  OneAppPermission = OneAppPermission;
  hasTenantPermission = true;
  errorLoadingRoles = false;
  errorMessage = '';
  onEmployeeDeleting = false;
  buttonAction = ButtonAction;
  tenant: string | undefined;

  constructor(
    private route: ActivatedRoute,
    private employeeService: EmployeeService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    private client: PublicTenantManagerClient,
    private readonly dialogService: ConfirmDialogService
  ) {}

  //Function to prevent routing to an other User or Page when there are changes in the form which are unsaved.
  async canDeactivate(): Promise<boolean> {
    //If the User deleted an Employee and he has to leave the route without confirming.
    if (this.onEmployeeDeleting) {
      this.onEmployeeDeleting = false;
      return true;
    }
    //Checks if the user did some changes in the forms.
    if (this.employeeForm.dirty) {
      return await opencanDeactivateDialog(this.dialogService);
    } else {
      return true;
    }
  }

  async ngOnInit(): Promise<void> {
    this.tenant = getActiveTenantFromSessionStorage();

    this.employeeChangedSubscription = this.employeeService.employeesChanged.subscribe(
      async (employees) => {
        if (!this.onEmployeeDeleting) {
          const currentEmployeeId = this.employeeId;
          const emp = await this.employeeService.getEmployee(this.employeeId);
          if (emp && currentEmployeeId === this.employeeId) {
            this.employee = emp;
            this.errorLoadingRoles = false;

            if (this.hasTenantPermission) {
              try {
                this.userRoles = (
                  await this.client
                    .tenants(this.tenant ? this.tenant : '')
                    .users(this.employee.userName)
                    .roles()
                    .get()
                ).map((role) => {
                  return role.name;
                });
              } catch (error) {
                this.handleTenantServiceError(error as HttpErrorResponse);
              }
              this.setEmployeeForm();
            }
          }
        }
      }
    );

    this.routeSub = this.route.params.subscribe(async (params) => {
      this.employeeId = params['employeeId'];
      const currentEmployeeId = this.employeeId;
      const emp = await this.employeeService.getEmployee(this.employeeId);
      if (emp && currentEmployeeId === this.employeeId) {
        this.employee = emp;
        this.errorLoadingRoles = false;
        this.errorMessage = '';
        if (this.hasTenantPermission) {
          try {
            this.userRoles = (
              await this.client
                .tenants(this.tenant ? this.tenant : '')
                .users(this.employee.userName)
                .roles()
                .get()
            ).map((role) => {
              return role.name;
            });
          } catch (error) {
            this.handleTenantServiceError(error as HttpErrorResponse);
          }
          this.setEmployeeForm();
        }
      }
    });

    this.employeeGroupsChangedSubscription = this.employeeService.employeeGroupsChanged.subscribe(
      (empGroups) => {
        this.employeeGroups = empGroups;
      }
    );

    this.employeeGroups = await this.employeeService.getEmployeeGroups();
    if (this.hasTenantPermission) {
      try {
        this.roles = await this.client
          .tenants(this.tenant ? this.tenant : '')
          .roles()
          .get();
      } catch (error) {
        this.handleTenantServiceError(error as HttpErrorResponse);
      }
      try {
        this.currentUserRoles = (
          await this.client
            .tenants(this.tenant ? this.tenant : '')
            .users((await this.authService.getUserInfo()).email)
            .roles()
            .get()
        ).map((role) => {
          return role.name;
        });
      } catch (error) {
        this.handleTenantServiceError(error as HttpErrorResponse);
      }
    }
  }

  handleTenantServiceError(error: HttpErrorResponse): any[] {
    this.errorLoadingRoles = true;
    this.errorMessage = error.message;
    if (error.status == 403) {
      this.hasTenantPermission = false;
    } else {
      this.hasTenantPermission = true;
    }
    return [];
  }

  setEmployeeForm() {
    if (this.userRoles) {
      this.employeeForm = new FormGroup({
        id: new FormControl(this.employee.employeeId),
        employee_group: new FormControl(this.employee.employeeGroup),
        roles: new FormControl(this.userRoles.slice() ?? []),
        start_screen_configuration: new FormControl(this.employee.startScreenConfiguration),

        name: new FormControl(this.employee.name ?? ''),
        email: new FormControl(this.employee.email == null ? '' : this.employee.email, [
          Validators.required,
          Validators.email
        ]),
        userName: new FormControl(this.employee.userName == null ? '' : this.employee.userName)
      });
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy(): void {
    this.employeeChangedSubscription.unsubscribe();
    this.newEmployeeSubscription.unsubscribe();
    this.employeeGroupsChangedSubscription.unsubscribe();
    this.routeSub.unsubscribe();
    this.employeeRouteChangeSub.unsubscribe();
    this.rolesChangedSubscription.unsubscribe();
  }

  onSubmit() {
    const employee: EmployeeModel = {
      employeeId: this.employee.employeeId,
      name: this.employeeForm.value.name,
      email: this.employeeForm.value.email,
      userName: this.employeeForm.value.userName,
      employeeGroup: this.employeeForm.value.employee_group,
      startScreenConfiguration: this.employeeForm.value.start_screen_configuration
    };
    let updatedRoles: string[] = this.employeeForm.value.roles;
    this.handleRoleUpdation(updatedRoles);
    this.employeeService.updateEmployee(employee);
  }

  handleRoleUpdation(updatedRoles: string[]) {
    let rolesToAdd: string[] = updatedRoles.filter((role) => !this.userRoles.includes(role));

    // this list includes all the roles for the user in active-tenant(could include roles from other applications)
    let rolesToDelete: string[] = this.userRoles.filter((role) => !updatedRoles.includes(role));
    // filter again for oneapp specific roles
    let appSpecificRolesToDelete: string[] = this.roles
      .filter((roles) => rolesToDelete.includes(roles.name))
      .map((userRole) => userRole.name);

    rolesToAdd.forEach((role) => {
      this.client
        .tenants(this.tenant ? this.tenant : '')
        .users(this.employee.userName)
        .roles()
        .assign(role);
    });
    appSpecificRolesToDelete.forEach((role) => {
      this.client
        .tenants(this.tenant ? this.tenant : '')
        .users(this.employee.userName)
        .roles()
        .unassign(role);
    });
    // this.tenantService.addUserRoles(this.employee.userName, rolesToAdd);
    // this.tenantService.deleteUserRoles(this.employee.userName, appSpecificRolesToDelete);
  }

  onEmployeeDelete() {
    const title = $localize`:delete process@@deleteProcess:Delete process`;
    const msg = $localize`:emplyoee delete confirmation text@@deleteConfirmationEmployee:Do you really want to delete this employee?`;
    const confirmButtonText = $localize`:button text delete@@buttonDelete:DELETE`;
    const cancelButtonText = $localize`:button text cancel@@buttonCancel:CANCEL`;

    const dialogData: ConfirmDeleteDialogData = {
      itemName: this.employee.name,
      title: title,
      message: msg,
      confirmText: toTitleCase(confirmButtonText),
      cancelText: toTitleCase(cancelButtonText)
    };
    this.dialogService.openConfirmDelete(dialogData).subscribe((result: boolean) => {
      if (result == true) {
        this.employeeService.deleteEmployee(this.employee);
        this.userRoles.forEach((role) => {
          this.client
            .tenants(this.tenant ? this.tenant : '')
            .users(this.employee.userName)
            .roles()
            .unassign(role);
        });
        // this.tenantService.deleteUserRoles(this.employee.userName, this.userRoles);
        this.onEmployeeDeleting = true;
      }
    });
  }

  isAdmin(): boolean {
    return this.currentUserRoles.some((r) => r.toLowerCase() === 'administrator');
  }
}
